import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import Profile from "../../Pages/Images/WhatsApp Image 2025-06-12 at 5.33.20 PM.webp";
import './CombinedProfileCard.css';
import Navbar from "../../Pages/Navbar/JobSeekerNavbar";
import {
  FaUser, FaBuilding, FaPhoneAlt, FaEye, FaRegHeart, FaShareAlt,
  FaEnvelope, FaIdCard, FaCheckCircle, FaUserCircle, FaLaptop,
  FaBriefcase, FaWhatsapp, FaTelegramPlane, FaMailBulk, FaBold,
  FaHome, FaCalendarAlt, FaExchangeAlt, FaMapMarkerAlt, FaFileAlt,
  FaPassport, FaMoneyBillAlt, FaInfoCircle, FaUtensils, FaStar,
  FaLanguage, FaCogs, FaUserFriends, FaGraduationCap,
  FaCar, FaUserNurse, FaStethoscope, FaUserTie, FaRunning, FaTools
} from 'react-icons/fa';
import BaseUrl from '../../ApiServices/BaseUrl';

const CombinedProfileCard = () => {
  const [profileData, setProfileData] = useState(null);
  const [doctorProfileData, setDoctorProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [isDoctorProfile, setIsDoctorProfile] = useState(false);

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const userId = user.id || '';

  // Helper function to safely parse JSON or return the value if it's already an object
  const safeParse = (field) => {
    if (!field) return [];
    if (typeof field === 'string') {
      try {
        return JSON.parse(field);
      } catch (e) {
        return field;
      }
    }
    return field;
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        // First fetch the basic job seeker data
        const response = await fetch(`${BaseUrl}/api/job-seeker/${userId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch profile data');
        }
        const data = await response.json();

        // Construct full URL for the profile photo if it exists
        if (data.profile_photo) {
          data.profile_photo = `${BaseUrl}${data.profile_photo}`;
        }

        setProfileData(data);

        // Determine job position and check if it's a doctor
        const jobPositions = safeParse(data.job_position);
        const isDoctor = Array.isArray(jobPositions) &&
          jobPositions.some(position =>
            position.toLowerCase().includes("doctor") ||
            position.toLowerCase().includes("physician")
          );

        setIsDoctorProfile(isDoctor);

        // If it's a doctor, fetch doctor profile data
        if (isDoctor) {
          try {
            const doctorResponse = await fetch(`${BaseUrl}/api/job-seeker/doctor/${userId}`);
            if (doctorResponse.ok) {
              const doctorData = await doctorResponse.json();

              // Parse JSON fields for doctor data
              const doctorJsonFields = [
                'dtr_preferred_country',
                'dtr_language_skilled',
                'dtr_country_doctor_license',
                'dtr_driving_license_country',
                'dtr_preferred_job_location',
                'dtr_previous_hospital_details',
                'dtr_before_worked_country',
                'dtr_certificate_options',
                'dtr_specialty_qualifications',
                'dtr_super_spl_qualifications',
                'dtr_fellowship_qualifications',
                'dtr_other_language_qualifications',
                'dtr_cultural_qualifications'
              ];

              doctorJsonFields.forEach(field => {
                if (doctorData[field]) {
                  try {
                    doctorData[field] = JSON.parse(doctorData[field]);
                  } catch (e) {
                    // If parsing fails, keep the original value
                  }
                }
              });

              setDoctorProfileData(doctorData);
            }
          } catch (doctorErr) {
            console.error('Error fetching doctor profile:', doctorErr);
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchProfileData();
    }
  }, [userId]);

  // Determine job position
  const getJobPosition = () => {
    if (!profileData?.job_position) return '';

    const positions = safeParse(profileData.job_position);

    if (Array.isArray(positions) && positions.length > 0) {
      return positions[0].toLowerCase();
    }
    return '';
  };

  const jobPosition = getJobPosition();

  // Check specific job types
  const isCook = () => jobPosition.includes('cook');
  const isTeacher = () => jobPosition.includes('teacher') || jobPosition.includes('tutor');
  const isNurse = () => jobPosition.toLowerCase().includes('nurse');
  const isDoctor = () => jobPosition.includes('doctor') || jobPosition.includes('physician');
  const isDriver = () => jobPosition.includes('driver');
  const isTailor = () => jobPosition.includes('tailor');
  const isTrainer = () => jobPosition.includes('trainer') || jobPosition.includes('coach');
  const isBeautician = () => jobPosition.includes('beautician');
  const isDomesticHelper = () => jobPosition.includes('domestic') || jobPosition.includes('helper') || jobPosition.includes('maid');

  if (loading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  if (error) {
    return <div className="text-center mt-5">Error: {error}</div>;
  }

  if (!profileData) {
    return <div className="text-center mt-5">No profile data found</div>;
  }

  // Parse fields safely
  const jobPositions = safeParse(profileData.job_position);
  const languages = safeParse(profileData.language);
  const mainSkills = safeParse(profileData.main_skills);
  const cookingSkills = safeParse(profileData.cooking_skills);
  const otherSkills = safeParse(profileData.other_skills);
  const personality = safeParse(profileData.personality);
  const experience = safeParse(profileData.experience);
  const preferredLocations = safeParse(profileData.preffered_job_locations);
  const education = safeParse(profileData.education);

  // Specialized fields parsing
  const drivingLicenseCountries = safeParse(profileData.driving_license_countries);
  const carTypesDriven = safeParse(profileData.car_types_driven);
  const previousCarsDriven = safeParse(profileData.previous_cars_driven);
  const nursingQualifications = safeParse(profileData.nursing_qualifications);
  const nursingPositions = safeParse(profileData.nursing_positions);
  const nursingJobCountries = safeParse(profileData.nursing_job_countries);
  const nursingLicenseCountries = safeParse(profileData.nursing_license_countries);
  const teachingSubjects = safeParse(profileData.subjects);
  const teachingAgeGroups = safeParse(profileData.age_groups);
  const teachingClasses = safeParse(profileData.classes);
  const beauticianServices = {
    henna: safeParse(profileData.henna_services),
    makeup: safeParse(profileData.makeup_services),
    skinCare: safeParse(profileData.skin_care_services),
    nail: safeParse(profileData.nail_services),
    hair: safeParse(profileData.hair_services),
    massage: safeParse(profileData.massage_spa_services)
  };
  const tailorSkills = {
    mode: profileData.tailor_mode,
    type: profileData.tailor_type,
    stitching: safeParse(profileData.stitching_preferences),
    fabric: safeParse(profileData.fabric_preference),
    style: safeParse(profileData.style_preference)
  };
  const trainingDetails = {
    mode: profileData.training_mode,
    sports: safeParse(profileData.sports_games),
    ageGroups: safeParse(profileData.sports_age_groups)
  };

  const formatSalary = (salary) => {
    if (!salary) return '';

    const salaryNum = typeof salary === 'string' ? parseFloat(salary) : salary;
    if (isNaN(salaryNum)) return salary;

    const roundedSalary = salaryNum % 1 >= 0.50 ? Math.ceil(salaryNum) : Math.floor(salaryNum);
    return roundedSalary % 1 === 0 ? roundedSalary.toString() : roundedSalary.toFixed(2);
  };

  const toggleReportForm = () => {
    setShowReportForm(!showReportForm);
  };

  // Render Doctor specific fields
  const renderDoctorSpecificFields = () => {
    if (!doctorProfileData) return null;

    return (
      <>
        {/* Doctor Qualification Information */}
        <div className="mb-3 desktop-section candidate-view-desktop-center">
          <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
            <FaGraduationCap size={20} className="me-2" />
            <span className="ms-2">Doctor Qualifications</span>
          </h6>
          <div className="candidate-view-content-wrapper-emp employer-candidate-viewdetails-workexperience">
            <div className="bg-light p-3 rounded candidate-view-experience-item">
              <ul className="mb-2 ps-3">
                {doctorProfileData.dtr_ug_qualification && (
                  <li><strong>Undergraduate:</strong> {doctorProfileData.dtr_ug_qualification}</li>
                )}
                {doctorProfileData.dtr_pg_qualification && (
                  <li><strong>Postgraduate:</strong> {doctorProfileData.dtr_pg_qualification}</li>
                )}
                {doctorProfileData.dtr_specialty_qualifications && Array.isArray(doctorProfileData.dtr_specialty_qualifications) && doctorProfileData.dtr_specialty_qualifications.length > 0 && (
                  <li><strong>Specialty:</strong> {doctorProfileData.dtr_specialty_qualifications.join(', ')}</li>
                )}
                {doctorProfileData.dtr_super_spl_qualifications && Array.isArray(doctorProfileData.dtr_super_spl_qualifications) && doctorProfileData.dtr_super_spl_qualifications.length > 0 && (
                  <li><strong>Super Specialty:</strong> {doctorProfileData.dtr_super_spl_qualifications.join(', ')}</li>
                )}
                {doctorProfileData.dtr_fellowship_qualifications && (
                  <li><strong>Fellowship:</strong> {doctorProfileData.dtr_fellowship_qualifications}</li>
                )}
                {doctorProfileData.dtr_other_qualifications && (
                  <li><strong>Other Qualifications:</strong> {doctorProfileData.dtr_other_qualifications}</li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Language Qualifications */}
        <div className="mb-3 desktop-section candidate-view-desktop-center">
          <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
            <FaLanguage size={20} className="me-2" />
            <span className="ms-2">Language Qualifications</span>
          </h6>
          <div className="candidate-view-content-wrapper-emp employer-candidate-viewdetails-workexperience">
            <div className="bg-light p-3 rounded candidate-view-experience-item">
              <ul className="mb-2 ps-3">
                {doctorProfileData.dtr_english_lang_qualifications && (
                  <li><strong>English:</strong> {doctorProfileData.dtr_english_lang_qualifications}</li>
                )}
                {doctorProfileData.dtr_other_language_qualifications && (
                  <li><strong>Other Languages:</strong> {doctorProfileData.dtr_other_language_qualifications}</li>
                )}
                {doctorProfileData.dtr_language_proficiency && (
                  <li><strong>Proficiency Level:</strong> {doctorProfileData.dtr_language_proficiency}</li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Cultural Qualifications */}
        {/* {doctorProfileData.dtr_cultural_qualifications && (
          <div className="mb-3 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaUserFriends size={20} className="me-2" />
              <span className="ms-2">Cultural Qualifications</span>
            </h6>
            <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
              {Array.isArray(doctorProfileData.dtr_cultural_qualifications) 
                ? doctorProfileData.dtr_cultural_qualifications.map((qual, index) => (
                    <span key={index} className="skill-tag">{qual}</span>
                  ))
                : <span className="skill-tag">{doctorProfileData.dtr_cultural_qualifications}</span>
              }
            </div>
          </div>
        )} */}

        {/* Certifications */}
        <div className="mb-3 desktop-section candidate-view-desktop-center">
          <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
            <FaCheckCircle size={20} className="me-2" />
            <span className="ms-2">Certifications</span>
          </h6>
          <div className="candidate-view-content-wrapper-emp employer-candidate-viewdetails-workexperience">
            <div className="bg-light p-3 rounded candidate-view-experience-item">
              <ul className="mb-2 ps-3">
                {doctorProfileData.dtr_prometric_certificate !== undefined && doctorProfileData.dtr_prometric_certificate !== null && (
                  <li><strong>Prometric Certificate:</strong> {doctorProfileData.dtr_prometric_certificate ? "Yes" : "No"}</li>
                )}
                {/* {doctorProfileData.dtr_prometric_certificate && doctorProfileData.dtr_prometric_issued_country && (
                  <li><strong>Prometric Issued Country:</strong> {doctorProfileData.dtr_prometric_issued_country}</li>
                )} */}
                {doctorProfileData.dtr_data_flow_certificate !== undefined && doctorProfileData.dtr_data_flow_certificate !== null && (
                  <li><strong>Dataflow Certificate:</strong> {doctorProfileData.dtr_data_flow_certificate ? "Yes" : "No"}</li>
                )}
                {/* {doctorProfileData.dtr_data_flow_certificate && doctorProfileData.dtr_data_flow_issued_by_country && (
                  <li><strong>Dataflow Issued Country:</strong> {doctorProfileData.dtr_data_flow_issued_by_country}</li>
                )} */}
              </ul>
            </div>
          </div>
        </div>

        {/* Medical Experience Information */}
        <div className="mb-3 desktop-section candidate-view-desktop-center">
          <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
            <FaStethoscope size={20} className="me-2" />
            <span className="ms-2">Medical Experience</span>
          </h6>
          <div className="candidate-view-content-wrapper-emp employer-candidate-viewdetails-workexperience">
            <div className="bg-light p-3 rounded candidate-view-experience-item">
              <ul className="mb-2 ps-3">
                {doctorProfileData.dtr_internship_clinical_experience && (
                  <li><strong>Post-Internship Clinical Experience:</strong> {doctorProfileData.dtr_internship_clinical_experience} years</li>
                )}
                {doctorProfileData.dtr_graduation_clinical_experience && (
                  <li><strong>Post-Graduation Clinical Experience:</strong> {doctorProfileData.dtr_graduation_clinical_experience} years</li>
                )}
                {doctorProfileData.dtr_years_of_experience_doctor && (
                  <li><strong>Total Years as Doctor:</strong> {doctorProfileData.dtr_years_of_experience_doctor} years</li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Current Hospital Information */}
        {doctorProfileData.dtr_currently_working_hospital_name && (
          <div className="mb-3 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaBuilding size={20} className="me-2" />
              <span className="ms-2">Current Hospital</span>
            </h6>
            <div className="candidate-view-content-wrapper-emp employer-candidate-viewdetails-workexperience">
              <div className="bg-light p-3 rounded candidate-view-experience-item">
                <ul className="mb-2 ps-3">
                  <li><strong>Hospital Name:</strong> {doctorProfileData.dtr_currently_working_hospital_name}</li>
                  {doctorProfileData.dtr_hospital_country && (
                    <li><strong>Country:</strong> {doctorProfileData.dtr_hospital_country}</li>
                  )}
                  {doctorProfileData.dtr_work_start_date && (
                    <li><strong>Start Date:</strong> {new Date(doctorProfileData.dtr_work_start_date).toLocaleDateString()}</li>
                  )}
                  {doctorProfileData.dtr_work_end_date && doctorProfileData.dtr_work_end_date !== "...On going" && (
                    <li><strong>End Date:</strong> {new Date(doctorProfileData.dtr_work_end_date).toLocaleDateString()}</li>
                  )}
                  {doctorProfileData.dtr_work_end_date === "...On going" && (
                    <li><strong>Status:</strong> Currently Working</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Previous Hospital Details */}
        {doctorProfileData.dtr_previous_hospital_details && Array.isArray(doctorProfileData.dtr_previous_hospital_details) && doctorProfileData.dtr_previous_hospital_details.length > 0 && (
          <div className="mb-3 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaBuilding size={20} className="me-2" />
              <span className="ms-2">Previous Hospital Experience</span>
            </h6>
            <div className="candidate-view-content-wrapper-emp employer-candidate-viewdetails-workexperience">
              {doctorProfileData.dtr_previous_hospital_details.map((hospital, index) => (
                <div key={index} className="bg-light p-3 rounded candidate-view-experience-item mb-2">
                  <ul className="mb-0 ps-3">
                    <li><strong>Hospital Name:</strong> {hospital.previous_hospital_name || hospital.hospital_name}</li>
                    {hospital.hospital_country_name && (
                      <li><strong>Country:</strong> {hospital.hospital_country_name}</li>
                    )}
                    {hospital.work_start_date && (
                      <li><strong>Start Date:</strong> {new Date(hospital.work_start_date).toLocaleDateString()}</li>
                    )}
                    {hospital.work_end_date && (
                      <li><strong>End Date:</strong> {new Date(hospital.work_end_date).toLocaleDateString()}</li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Preferred Countries */}
        {doctorProfileData.dtr_preferred_country && Array.isArray(doctorProfileData.dtr_preferred_country) && doctorProfileData.dtr_preferred_country.length > 0 && (
          <div className="mb-3 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaMapMarkerAlt size={20} className="me-2" />
              <span className="ms-2">Preferred Countries</span>
            </h6>
            <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
              {doctorProfileData.dtr_preferred_country.map((country, index) => (
                <span key={index} className="skill-tag">{country}</span>
              ))}
            </div>
          </div>
        )}

        {/* Language Skills */}
        {doctorProfileData.dtr_language_skilled && Array.isArray(doctorProfileData.dtr_language_skilled) && doctorProfileData.dtr_language_skilled.length > 0 && (
          <div className="mb-3 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaLanguage size={20} className="me-2" />
              <span className="ms-2">Language Skills</span>
            </h6>
            <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
              {doctorProfileData.dtr_language_skilled.map((language, index) => (
                <span key={index} className="skill-tag">{language}</span>
              ))}
            </div>
          </div>
        )}

        {/* Doctor License Countries */}
        {doctorProfileData.dtr_country_doctor_license && Array.isArray(doctorProfileData.dtr_country_doctor_license) && doctorProfileData.dtr_country_doctor_license.length > 0 && (
          <div className="mb-3 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaIdCard size={20} className="me-2" />
              <span className="ms-2">Doctor License Countries</span>
            </h6>
            <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
              {doctorProfileData.dtr_country_doctor_license.map((license, index) => (
                <span key={index} className="skill-tag">
                  {license.country_name || license}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Driving License Countries */}
        {doctorProfileData.dtr_driving_license_country && Array.isArray(doctorProfileData.dtr_driving_license_country) && doctorProfileData.dtr_driving_license_country.length > 0 && (
          <div className="mb-3 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaCar size={20} className="me-2" />
              <span className="ms-2">Driving License Countries</span>
            </h6>
            <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
              {doctorProfileData.dtr_driving_license_country.map((country, index) => (
                <span key={index} className="skill-tag">{country}</span>
              ))}
            </div>
          </div>
        )}

        {/* Countries Worked Before */}
        {doctorProfileData.dtr_before_worked_country && Array.isArray(doctorProfileData.dtr_before_worked_country) && doctorProfileData.dtr_before_worked_country.length > 0 && (
          <div className="mb-3 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaMapMarkerAlt size={20} className="me-2" />
              <span className="ms-2">Countries Worked Before</span>
            </h6>
            <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
              {doctorProfileData.dtr_before_worked_country.map((country, index) => (
                <span key={index} className="skill-tag">{country}</span>
              ))}
            </div>
          </div>
        )}

        {/* Preferred Job Locations */}
        {doctorProfileData.dtr_preferred_job_location && Array.isArray(doctorProfileData.dtr_preferred_job_location) && doctorProfileData.dtr_preferred_job_location.length > 0 && (
          <div className="mb-3 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaMapMarkerAlt size={20} className="me-2" />
              <span className="ms-2">Preferred Job Locations</span>
            </h6>
            <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
              {doctorProfileData.dtr_preferred_job_location.map((location, index) => (
                <span key={index} className="skill-tag">{location}</span>
              ))}
            </div>
          </div>
        )}

        {/* Job Position Details */}
        <div className="mb-3 desktop-section candidate-view-desktop-center">
          <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
            <FaBriefcase size={20} className="me-2" />
            <span className="ms-2">Job Position Details</span>
          </h6>
          <div className="candidate-view-content-wrapper-emp employer-candidate-viewdetails-workexperience">
            <div className="bg-light p-3 rounded candidate-view-experience-item">
              <ul className="mb-2 ps-3">
                {doctorProfileData.dtr_medical_job_position && (
                  <li><strong>Medical Job Position:</strong> {doctorProfileData.dtr_medical_job_position}</li>
                )}
                {doctorProfileData.dtr_surgical_job_position && (
                  <li><strong>Surgical Job Position:</strong> {doctorProfileData.dtr_surgical_job_position}</li>
                )}
                {doctorProfileData.dtr_other_medical_job_position && (
                  <li><strong>Other Medical Position:</strong> {doctorProfileData.dtr_other_medical_job_position}</li>
                )}
                {doctorProfileData.dtr_academic_job_position && (
                  <li><strong>Academic Position:</strong> {doctorProfileData.dtr_academic_job_position}</li>
                )}
                {doctorProfileData.dtr_administrative_leadership_job_position && (
                  <li><strong>Administrative Position:</strong> {doctorProfileData.dtr_administrative_leadership_job_position}</li>
                )}
                {doctorProfileData.dtr_job_position_for_applying && (
                  <li><strong>Applying For:</strong> {doctorProfileData.dtr_job_position_for_applying}</li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Job Start Date */}
        {doctorProfileData.dtr_job_start_date && (
          <div className="mb-3 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaCalendarAlt size={20} className="me-2" />
              <span className="ms-2">Preferred Job Start Date</span>
            </h6>
            <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
              <span className="skill-tag">
                {new Date(doctorProfileData.dtr_job_start_date).toLocaleDateString()}
              </span>
            </div>
          </div>
        )}

        {/* Salary Expectation */}
        {/* {(doctorProfileData.dtr_min_monthly_sal || doctorProfileData.dtr_max_monthly_sal) && (
          <div className="mb-4 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaMoneyBillAlt size={20} className="me-2" />
              <span className="ms-2">Salary Expectation</span>
            </h6>
            <div className="d-flex flex-wrap gap-3 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
              {doctorProfileData.dtr_min_monthly_sal && doctorProfileData.dtr_max_monthly_sal && (
                <span className="skill-tag">
                  {formatSalary(doctorProfileData.dtr_min_monthly_sal)} - {formatSalary(doctorProfileData.dtr_max_monthly_sal)} {doctorProfileData.dtr_country_currency || ''}
                </span>
              )}
            </div>
          </div>
        )} */}

        {/* Day Off Preference */}
        {doctorProfileData.dtr_day_off_preference && (
          <div className="mb-4 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaCalendarAlt size={20} className="me-2" />
              <span className="ms-2">Day Off Preference</span>
            </h6>
            <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
              <span className="skill-tag">
                {doctorProfileData.dtr_day_off_preference}
              </span>
            </div>
          </div>
        )}

        {/* Accommodation Preference */}
        {doctorProfileData.dtr_accommodation_preference && (
          <div className="mb-4 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaHome size={20} className="me-2" />
              <span className="ms-2">Accommodation Preference</span>
            </h6>
            <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
              <span className="skill-tag">
                {doctorProfileData.dtr_accommodation_preference}
              </span>
            </div>
          </div>
        )}

        {/* Reference Letter */}
        {doctorProfileData.reference_letter !== undefined && (
          <div className="mb-4 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaFileAlt size={20} className="me-2" />
              <span className="ms-2">Reference Letter</span>
            </h6>
            <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
              <span className="skill-tag">
                {doctorProfileData.reference_letter ? 'Yes, I have' : 'No'}
              </span>
            </div>
          </div>
        )}

        {/* Valid Passport */}
        {doctorProfileData.dtr_valid_passport !== undefined && (
          <div className="mb-4 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaPassport size={20} className="me-2" />
              <span className="ms-2">Passport</span>
            </h6>
            <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
              <span className="skill-tag">
                {doctorProfileData.dtr_valid_passport ? 'Yes, I have Passport' : 'No'}
              </span>
            </div>
          </div>
        )}

        {/* Doctor Resume Description */}
        {doctorProfileData.dtr_resume_description && (
          <div className="mb-4 desktop-section candidate-view-desktop-center">
            <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
              <FaInfoCircle size={20} className="me-2" />
              <span className="ms-2">About Me</span>
            </h6>
            <div className="employer-candidate-viewdetails-aboutme candidateview-view-about-box">
              <div className="about-me-box p-2 border rounded candidate-view-about-box" style={{ textAlign: "justify" }}>
                {doctorProfileData.dtr_resume_description}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Render Cook specific fields
  const renderCookSpecificFields = () => (
    <>
      {/* Cooking Skills */}
      <div className="mb-3 desktop-section candidate-view-desktop-center">
        <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
          <FaUtensils size={20} className="me-2" />
          <span className="ms-2">Cooking Skills</span>
        </h6>
        <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
          {cookingSkills.map((item, index) => (
            <span key={index} className="skill-tag">{item}</span>
          ))}
        </div>
      </div>

      {/* Other Skills */}
      <div className="mb-3 desktop-section candidate-view-desktop-center">
        <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
          <FaStar size={20} className="me-2" />
          <span className="ms-2">Other Skills</span>
        </h6>
        <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
          {otherSkills.map((item, index) => (
            <span key={index} className="skill-tag">{item}</span>
          ))}
        </div>
      </div>
    </>
  );

  // Render Teacher specific fields
  const renderTeacherSpecificFields = () => (
    <>
      {/* Teaching Subjects */}
      <div className="mb-3 desktop-section candidate-view-desktop-center">
        <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
          <FaGraduationCap size={20} className="me-2" />
          <span className="ms-2">Teaching Subjects</span>
        </h6>
        <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
          {teachingSubjects.map((subject, index) => (
            <span key={index} className="skill-tag">{subject}</span>
          ))}
        </div>
      </div>

      {/* Teaching Levels */}
      <div className="mb-3 desktop-section candidate-view-desktop-center">
        <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
          <FaUser size={20} className="me-2" />
          <span className="ms-2">Teaching Levels</span>
        </h6>
        <div className="d-flex flex-wrap gap-3 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
          {teachingAgeGroups.length > 0 && (
            <div>
              <strong>Age Groups:</strong>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {teachingAgeGroups.map((age, index) => (
                  <span key={index} className="skill-tag">{age}</span>
                ))}
              </div>
            </div>
          )}
          {teachingClasses.length > 0 && (
            <div>
              <strong>Classes:</strong>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {teachingClasses.map((cls, index) => (
                  <span key={index} className="skill-tag">{cls}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  // Render Nurse specific fields
  const renderNurseSpecificFields = () => (
    <>
      {/* Nursing Qualifications */}
      <div className="mb-3 desktop-section candidate-view-desktop-center">
        <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
          <FaStethoscope size={20} className="me-2" />
          <span className="ms-2">Nursing Qualifications</span>
        </h6>
        <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
          {nursingQualifications.map((qual, index) => (
            <span key={index} className="skill-tag">{qual}</span>
          ))}
        </div>
      </div>

      {/* Nursing Positions */}
      <div className="mb-3 desktop-section candidate-view-desktop-center">
        <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
          <FaUserNurse size={20} className="me-2" />
          <span className="ms-2">Nursing Positions</span>
        </h6>
        <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
          {nursingPositions.map((position, index) => (
            <span key={index} className="skill-tag">{position}</span>
          ))}
        </div>
      </div>

      {/* Nursing Experience Countries */}
      {nursingJobCountries.length > 0 && (
        <div className="mb-3 desktop-section candidate-view-desktop-center">
          <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
            <FaMapMarkerAlt size={20} className="me-2" />
            <span className="ms-2">Countries Worked as Nurse</span>
          </h6>
          <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
            {nursingJobCountries.map((country, index) => (
              <span key={index} className="skill-tag">{country}</span>
            ))}
          </div>
        </div>
      )}

      {/* Prometric Certificate Details */}
      {(profileData.has_prometric_certificate === 1 || profileData.has_prometric_certificate === true) && (
        <div className="mb-3 desktop-section candidate-view-desktop-center">
          <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
            <FaCheckCircle size={20} className="me-2" />
            <span className="ms-2">Prometric Certificate</span>
          </h6>
          <div className="d-flex flex-wrap gap-3 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
            <div className="bg-light p-3 rounded">
              <ul className="mb-0 ps-3">
                {profileData.prometric_issued_country && (
                  <li><strong>Issued Country:</strong> {profileData.prometric_issued_country}</li>
                )}
                {profileData.prometric_issue_date && (
                  <li><strong>Issue Date:</strong> {new Date(profileData.prometric_issue_date).toLocaleDateString()}</li>
                )}
                {profileData.prometric_expiry_date && (
                  <li><strong>Expiry Date:</strong> {new Date(profileData.prometric_expiry_date).toLocaleDateString()}</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Dataflow Certificate Details */}
      {(profileData.has_dataflow_certificate === 1 || profileData.has_dataflow_certificate === true) && (
        <div className="mb-3 desktop-section candidate-view-desktop-center">
          <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
            <FaCheckCircle size={20} className="me-2" />
            <span className="ms-2">Dataflow Certificate</span>
          </h6>
          <div className="d-flex flex-wrap gap-3 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
            <div className="bg-light p-3 rounded">
              <ul className="mb-0 ps-3">
                {profileData.dataflow_issued_country && (
                  <li><strong>Issued Country:</strong> {profileData.dataflow_issued_country}</li>
                )}
                {profileData.dataflow_issue_date && (
                  <li><strong>Issue Date:</strong> {new Date(profileData.dataflow_issue_date).toLocaleDateString()}</li>
                )}
                {profileData.dataflow_expiry_date && (
                  <li><strong>Expiry Date:</strong> {new Date(profileData.dataflow_expiry_date).toLocaleDateString()}</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Nursing License Countries */}
      {nursingLicenseCountries.filter(lib => lib && lib !== "I Don't have License" && lib !== "Not Licensed").length > 0 ? (
        <div className="mb-3 desktop-section candidate-view-desktop-center">
          <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
            <FaIdCard size={20} className="me-2" />
            <span className="ms-2">Nursing License Countries</span>
          </h6>
          <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
            {nursingLicenseCountries
              .filter(lib => lib && lib !== "I Don't have License" && lib !== "Not Licensed")
              .map((license, index) => (
                <span key={index} className="skill-tag">{license}</span>
              ))}
          </div>
        </div>
      ) : (
        <div className="mb-3 desktop-section candidate-view-desktop-center">
          <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
            <FaIdCard size={20} className="me-2" />
            <span className="ms-2">Nursing License</span>
          </h6>
          <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
            <span className="skill-tag">Not Licensed</span>
          </div>
        </div>
      )}
    </>
  );

  // Render Driver specific fields
  const renderDriverSpecificFields = () => (
    <>
      {/* Driving License */}
      <div className="mb-3 desktop-section candidate-view-desktop-center">
        <h6 className="fw-bold text-primary d-flex align-items-center fields-headingfont">
          <FaCar size={20} className="me-2" />
          <span className="ms-2">Driving License</span>
        </h6>
        <div className="d-flex flex-wrap gap-3 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
          <div>
            <strong>Countries:</strong>
            <div className="d-flex flex-wrap gap-2 mt-1">
              {(() => {
                const filtered = drivingLicenseCountries.filter(
                  (c) => c && c !== "I don't have driving license" && c !== "I Don't have License" && c !== "Not Licensed"
                );
                return filtered.length > 0 ? (
                  filtered.map((country, index) => (
                    <span key={index} className="skill-tag">{country}</span>
                  ))
                ) : (
                  <span className="skill-tag">Not Licensed</span>
                );
              })()}
            </div>
          </div>
          {carTypesDriven.length > 0 && (
            <div>
              <strong>Car Types:</strong>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {carTypesDriven.map((type, index) => (
                  <span key={index} className="skill-tag">{type}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
      <Navbar />
      <div className="combined-profile-details-container p-3" style={{ marginTop: '55px' }}>
        <div className="combined-profile-details-card bg-white shadow rounded-4">
          {/* Divider */}
          <hr className="profile-divider" />

          {/* Second Profile Section */}
          <div className="profile-section p-3">
            {/* Top Tags */}
            <div className='bg-light'>
              <div className="d-flex justify-content-between px-2 mb-2">
                {/* <span className="profile-badge-green">New</span>
                <span className="profile-badge-green">Energetic</span> */}
              </div>

              {/* Profile Image */}
              <div className="profile-img-wrapper-img mx-auto mb-3" style={{
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <img
                  src={profileData.profile_photo || Profile}
                  alt="Profile"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center'
                  }}
                />
              </div>

              {/* Bottom Tags */}
              <div className="d-flex justify-content-between px-3 mb-2">
                <span className="profile-label-green">Gulf Exp.</span>
                <span className="profile-label-green">{profileData.accommodation_preference}</span>
              </div>
            </div>

            {/* Info Section */}
            <div className="profile-info bg-light p-3 rounded-3">
              <h5 className="fw-bold mb-2">
                • {profileData.first_name}{' '}{profileData.last_name}, {profileData.age} Years Old
              </h5>
              <p className="mb-0 fw-bold">
                Job Position - <span className="fw-bold">{jobPositions.join(', ')}</span>
              </p>

              {/* Nurse Specific Certifications - Quick Summary at Top */}
              {isNurse() && (
                <div className="nurse-top-certifications mt-1">
                  {(profileData.has_prometric_certificate === 1 || profileData.has_prometric_certificate === true) && (
                    <span className="badge bg-success me-2 mb-1">
                      <FaCheckCircle className="me-1" /> Prometric Certified
                    </span>
                  )}
                  {(profileData.has_dataflow_certificate === 1 || profileData.has_dataflow_certificate === true) && (
                    <span className="badge bg-info me-2 mb-1">
                      <FaCheckCircle className="me-1" /> Dataflow Verified
                    </span>
                  )}
                  {nursingLicenseCountries.filter(lib => lib && lib !== "I Don't have License" && lib !== "Not Licensed").length > 0 && (
                    <span className="badge bg-warning text-dark mb-1">
                      <FaIdCard className="me-1" /> Licensed Nurse
                    </span>
                  )}
                </div>
              )}

              <p className="mb-0">Total Experience - {profileData.total_work_experience === 'Fresher (0 Year)' || profileData.total_work_experience === '0' || profileData.total_work_experience === 0 ? 'Fresher' : profileData.total_work_experience}</p>
              <p className="mb-1">Current Location - {profileData.current_country}</p>
            </div>
          </div>

          {/* Divider */}
          <hr className="profile-divider" />

          {/* Main Content Section */}
          <div className="cook-details-section p-3">
            {/* Section: Personal Information */}
            <div className=" desktop-section candidate-view-desktop-center">
              <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                <FaUser size={20} className="me-2" />
                <span className="ms-2">Personal Information</span>
              </h6>
              <div className="cook-info-box candidate-view-content-wrapper-emp candidate-view-skill-info">
                <ul className="list-group list-group-flush candidate-view-skill-info">
                  <li className="list-group-item cook-info-item">
                    <span className="text-success">•</span> Gender - {profileData.gender}
                  </li>
                  <li className="list-group-item cook-info-item">
                    <span className="text-success">•</span> Religion - {profileData.religion}
                  </li>
                  <li className="list-group-item cook-info-item">
                    <span className="text-success">•</span> Marital Status - {profileData.marital_status}
                  </li>
                  <li className="list-group-item cook-info-item">
                    <span className="text-success">•</span> Nationality - {profileData.nationality}
                  </li>
                  <li className="list-group-item cook-info-item">
                    <span className="text-success">•</span> Qualification - {education[0]?.level || 'Not specified'}
                  </li>
                  <li className="list-group-item cook-info-item">
                    <span className="text-success">•</span> Language - {languages.map(lang => lang.language).join(', ') || 'Not specified'}
                  </li>
                  <li className="list-group-item cook-info-item">
                    <span className="text-success">•</span> Experience - {profileData.total_work_experience === 'Fresher (0 Year)' || profileData.total_work_experience === '0' || profileData.total_work_experience === 0 ? 'Fresher' : profileData.total_work_experience}
                  </li>
                </ul>
              </div>
            </div>

            {/* Language Sections */}
            {languages.length > 0 && languages.map((lang, idx) => (
              <div className="mb-3 desktop-section candidate-view-desktop-center" key={idx}>
                <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                  <FaLanguage size={20} className="me-2" />
                  <span className="ms-2">{lang.language} Language</span>
                </h6>
                <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                  {lang.speaking_level && <span className="language-tag">Speak: {lang.speaking_level}</span>}
                  {lang.can_read && <span className="language-tag">Read</span>}
                  {lang.can_write && <span className="language-tag">Write</span>}
                </div>
              </div>
            ))}

            {/* Section: Main Skills */}
            {mainSkills.length > 0 && (
              <div className="mb-3 desktop-section candidate-view-desktop-center">
                <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                  <FaCogs size={20} className="me-2" />
                  <span className="ms-2">Main Skills</span>
                </h6>
                <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                  {mainSkills.map((skill, idx) => (
                    <span key={idx} className="skill-tag">{skill}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Job Position Specific Fields */}
            {isCook() && renderCookSpecificFields()}
            {isTeacher() && renderTeacherSpecificFields()}
            {isNurse() && renderNurseSpecificFields()}
            {isDoctor() && renderDoctorSpecificFields()}
            {isDriver() && renderDriverSpecificFields()}

            {/* Personality */}
            {personality.length > 0 && (
              <div className="mb-3 desktop-section candidate-view-desktop-center">
                <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                  <FaUserFriends size={20} className="me-2" />
                  <span className="ms-2">Personality</span>
                </h6>
                <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                  {personality.map((trait, index) => (
                    <span key={index} className="skill-tag">{trait}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Work Experience */}
            <div className="mb-3 desktop-section candidate-view-desktop-center">
              <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                <FaBriefcase size={20} className="me-2" />
                <span className="ms-2">Work Experience</span>
              </h6>

              <div className="candidate-view-content-wrapper-emp employer-candidate-viewdetails-workexperience">
                {experience.length > 0 ? (
                  experience.map((exp, expIdx) => {
                    const countryDisplay = Array.isArray(exp.country)
                      ? exp.country.join(', ')
                      : exp.country || '';
                    const jobPositionDisplay = Array.isArray(exp.job_position)
                      ? exp.job_position.join(', ')
                      : exp.job_position || '';
                    const dutiesDisplay = Array.isArray(exp.house_duties)
                      ? exp.house_duties.join(', ')
                      : exp.house_duties || '';

                    return (
                      <div className="bg-light p-3 rounded candidate-view-experience-item" key={expIdx}>
                        <ul className="mb-2 ps-3">
                          {exp.join_month && exp.join_year && (
                            <li>{exp.join_month} {exp.join_year} - {exp.exit_month || 'Present'} {exp.exit_year || ''}</li>
                          )}
                          <li><strong>Position:</strong> {jobPositionDisplay}</li>
                          {countryDisplay && <li><strong>Country:</strong> {countryDisplay}</li>}
                          {exp.employer_nationality && <li><strong>Employer:</strong> {exp.employer_nationality} {exp.employer_type === 'Family' ? 'Family' : ''}</li>}
                          {dutiesDisplay && <li><strong>Duties:</strong> {dutiesDisplay}</li>}
                        </ul>
                      </div>
                    );
                  })
                ) : (
                  <div className="bg-light p-3 rounded mt-3 candidate-view-experience-item">
                    <p>No work experience listed</p>
                  </div>
                )}
              </div>
            </div>

            {/* Salary Preference Section */}
            <div className="mb-4 desktop-section candidate-view-desktop-center">
              <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                <FaMoneyBillAlt size={20} className="me-2" />
                <span className="ms-2">My Salary Expectation</span>
              </h6>

              <div className="d-flex flex-wrap gap-3 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                {profileData.expected_monthly_salary_qar && (
                  <span className="skill-tag">
                    Qatar – {formatSalary(profileData.expected_monthly_salary_qar)} QR
                  </span>
                )}

                {profileData.expected_monthly_salary_usd && (
                  <span className="skill-tag">
                    USD – ${formatSalary(profileData.expected_monthly_salary_usd)}
                  </span>
                )}

                {profileData.expected_monthly_salary && (
                  <span className="skill-tag">
                    Local – {formatSalary(profileData.expected_monthly_salary)}
                  </span>
                )}
              </div>
            </div>

            {/* Accommodation */}
            {profileData.accommodation_preference && (
              <div className="mb-4 desktop-section candidate-view-desktop-center">
                <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                  <FaHome size={20} className="me-2" />
                  <span className="ms-2">Accommodation</span>
                </h6>

                <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                  {(Array.isArray(profileData.accommodation_preference)
                    ? profileData.accommodation_preference
                    : profileData.accommodation_preference
                      ?.split(',')
                      .filter(Boolean)
                  )?.map((item, index) => (
                    <span key={index} className="skill-tag">
                      {item.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Day Off */}
            {profileData.day_off_preference && (
              <div className="mb-4 desktop-section candidate-view-desktop-center">
                <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                  <FaCalendarAlt size={20} className="me-2" />
                  <span className="ms-2">Day Off</span>
                </h6>

                <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                  {(Array.isArray(profileData.day_off_preference)
                    ? profileData.day_off_preference
                    : profileData.day_off_preference
                      ?.split(',')
                      .filter(Boolean)
                  )?.map((item, index) => (
                    <span key={index} className="skill-tag">
                      {item.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Work Status */}
            {profileData.current_work_status && (
              <div className="mb-4 desktop-section candidate-view-desktop-center">
                <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                  <FaExchangeAlt size={20} className="me-2" />
                  <span className="ms-2">Current Work Status</span>
                </h6>

                <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                  {(Array.isArray(profileData.current_work_status)
                    ? profileData.current_work_status
                    : profileData.current_work_status
                      ?.split(',')
                      .filter(Boolean)
                  )?.map((item, index) => (
                    <span key={index} className="skill-tag">
                      {item.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Preferred Locations */}
            {(preferredLocations.length > 0 || profileData.other_job_location) && (
              <div className="mb-4 desktop-section candidate-view-desktop-center">
                <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                  <FaMapMarkerAlt size={20} className="me-2" />
                  <span className="ms-2">Prefer Location</span>
                </h6>

                <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                  {preferredLocations.map((loc, index) => (
                    <span key={index} className="skill-tag">
                      {loc.trim()}
                    </span>
                  ))}

                  {profileData.other_job_location && (
                    <span className="skill-tag">
                      {profileData.other_job_location}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* NOC Letter */}
            <div className="mb-4 desktop-section candidate-view-desktop-center">
              <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                <FaFileAlt size={20} className="me-2" />
                <span className="ms-2">NOC letter / Transfer letter</span>
              </h6>

              <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                <span className="skill-tag">
                  {profileData.noc_or_transfer_letter ? 'Yes, I have' : 'No'}
                </span>
              </div>
            </div>

            {/* Visa Status */}
            {profileData.visa_type && (
              <div className="mb-4 desktop-section candidate-view-desktop-center">
                <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                  <FaPassport size={20} className="me-2" />
                  <span className="ms-2">Visa Status</span>
                </h6>

                <div className="d-flex flex-wrap gap-2 mt-3 candidate-view-content-wrapper-emp candidate-view-tags-container employer-candidate-viewdetails">
                  <span className="skill-tag">
                    {profileData.visa_type}
                  </span>
                </div>
              </div>
            )}

            {/* About Me */}
            {profileData.resume_description && (
              <div className="mb-4 desktop-section candidate-view-desktop-center">
                <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                  <FaInfoCircle size={20} className="me-2" />
                  <span className="ms-2">About Me</span>
                </h6>
                <div className="employer-candidate-viewdetails-aboutme candidateview-view-about-box">
                  <div className="about-me-box p-2 border rounded candidate-view-about-box" style={{ textAlign: "justify" }}>
                    {profileData.resume_description}
                  </div>
                </div>
              </div>
            )}

            {/* YouTube Video Link */}
            {profileData.upload_video && (
              <div className="mb-3 desktop-section candidate-view-desktop-center">
                <h6 className="fw-bold text-primary d-flex align-items-center candidate-view-section-title">
                  <FaGraduationCap size={20} className="me-2" />
                  <span className="ms-2">YouTube Video Link</span>
                </h6>
                <div className="candidate-view-content-wrapper-emp">
                  <div className="badge-pill-container-youtube">
                    <a href={profileData.upload_video} target="_blank" rel="noopener noreferrer">
                      Click here to watch introduction video
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>

          {showReportForm && (
            <div className="report-job-section p-3">
              <div className="shadow p-4" style={{ width: '60%', marginLeft: '20%' }}>
                <h5 className="mb-3">Report this job ad</h5>

                <form>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">
                      Your email address
                    </label>
                    <input
                      type="email"
                      className="form-control report-job-input"
                      id="email"
                      placeholder="Enter your email"
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="reason" className="form-label">
                      Reason for reporting job
                    </label>
                    <select className="form-select report-job-select" id="reason">
                      <option>Please select</option>
                      <option>Fraudulent</option>
                      <option>Misleading</option>
                      <option>Discriminatory</option>
                      <option>Other</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="details" className="form-label">
                      Please describe your issues with this listing
                    </label>
                    <textarea
                      className="form-control report-job-textarea"
                      id="details"
                      rows="4"
                    ></textarea>
                  </div>

                  <div className="report-job-warning">
                    ⚠️ To help fast track investigation, please include here any other relevant details that prompted you to report this job ad as fraudulent / misleading / discriminatory.
                  </div>

                  {/* Buttons stacked one under the other */}
                  <div className="d-flex flex-column my-5">
                    <button type="submit" className="btn btn-primary report-job-button w-100 mb-3">
                      Report job
                    </button>

                    <button
                      type="button"
                      className="btn btn-link report-job-cancel w-100"
                      onClick={toggleReportForm}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CombinedProfileCard;